import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatIndian, formatApiError } from "@/lib/utils";
import { Lock, Search, Plus, Minus, Check, Copy, Download, X } from "lucide-react";
import CatalogFilterBar, { DEFAULT_FILTERS, applyFiltersAndSort, priceBounds } from "@/components/CatalogFilterBar";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// CS 21.72 -- Categories are now fetched dynamically from the surface's
// FULL catalog_products dataset (never hardcoded). The kept-in-file "All"
// pseudo-bucket stays as the neutral first chip.
const PURITIES = ["9kt", "14kt", "18kt"];

/**
 * Public selection catalogue for the Distribution module.
 *
 *  kind: 'distributor' | 'retailer'
 *    - distributor link: Owner sees Distributor + Retailer + Customer MRP. Staff sees Retailer + MRP.
 *    - retailer link:    Owner sees Retailer + MRP.  Staff sees MRP only.
 *
 *  The visible columns and GP figures are decided by the BACKEND based on which PIN
 *  was entered (owner vs staff). The UI only renders whatever the API returns.
 */
export default function DistributionCollection({ kind = "distributor" }) {
  const { token } = useParams();
  const STORAGE_PIN_KEY = `dist_${kind}_sess_${token}`;
  const STORAGE_SEL_KEY = `dist_${kind}_sel_${token}`;
  const STORAGE_COL_KEY = `dist_${kind}_col_${token}`;
  const [pin, setPin] = useState(() => { sessionStorage.removeItem(`dist_${kind}_pin_${token}`); return sessionStorage.getItem(STORAGE_PIN_KEY) || ""; });
  const [pinInput, setPinInput] = useState("");
  const [viewer, setViewer] = useState(null);
  const [authError, setAuthError] = useState("");
  const [purity, setPurity] = useState("9kt");
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [goldRate24kt, setGoldRate24kt] = useState(null);  // CS 20.5 — driving 24kt gold rate
  // CS 21.72 -- Dynamic category list for this collection (with counts).
  // Fetched once viewer authenticates. Alias-normalised server-side so
  // "Nosepin" + "Nose Pin" collapse into one chip.
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [recipientName, setRecipientName] = useState(null); // CS 20.8 — per-recipient personalisation
  const [linkInactive, setLinkInactive] = useState(false);  // CS 20.8 — 410 from backend
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [qty, setQty] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_SEL_KEY) || "{}"); } catch { return {}; }
  });
  // CS 20.2 — per-design gold colour selection ('Yellow' | 'White'). Default Yellow on first sight.
  const [colour, setColour] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_COL_KEY) || "{}"); } catch { return {}; }
  });
  const [showReview, setShowReview] = useState(false);

  // Persist quantities locally (Phase 1: client-only)
  useEffect(() => {
    localStorage.setItem(STORAGE_SEL_KEY, JSON.stringify(qty));
  }, [qty, STORAGE_SEL_KEY]);
  useEffect(() => {
    localStorage.setItem(STORAGE_COL_KEY, JSON.stringify(colour));
  }, [colour, STORAGE_COL_KEY]);

  // Load meta on mount
  useEffect(() => {
    axios.get(`${PUBLIC_API}/public/dist/${kind}/${token}`)
      .then((r) => {
        setMeta(r.data);
        if (r.data?.recipient_name) setRecipientName(r.data.recipient_name);
      })
      .catch((e) => {
        if (e.response?.status === 410) {
          setLinkInactive(true);
        } else {
          setAuthError("This link is invalid or has been deactivated.");
        }
      });
  }, [kind, token]);

  // Auto-verify stored PIN
  useEffect(() => {
    if (!pin) return;
    axios.post(`${PUBLIC_API}/public/dist/${kind}/${token}/verify-pin`, { pin })
      .then((r) => {
        setViewer(r.data.viewer);
        if (r.data?.recipient_name) setRecipientName(r.data.recipient_name);
      })
      .catch((e) => {
        setPin("");
        sessionStorage.removeItem(STORAGE_PIN_KEY);
        if (e.response?.status === 410) setLinkInactive(true);
      });
  }, [pin, kind, token, STORAGE_PIN_KEY]);

  // Load products whenever viewer/filters change
  const loadProducts = useCallback(async () => {
    if (!viewer) return;
    setItems(null);
    try {
      const { data } = await axios.get(`${PUBLIC_API}/public/dist/${kind}/${token}/products`, {
        params: {
          pin,
          purity: purity.toUpperCase(),
          category: category === "All" ? undefined : category,
          search: search || undefined,
        },
      });
      setItems(data.items || []);
      if (typeof data.gold_rate_24kt === "number") setGoldRate24kt(data.gold_rate_24kt);
      if (data?.recipient_name) setRecipientName(data.recipient_name);
    } catch (e) {
      if (e.response?.status === 410) {
        setLinkInactive(true);
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      setItems([]);
    }
  }, [viewer, kind, token, pin, purity, category, search]);

  useEffect(() => {
    const t = setTimeout(loadProducts, 250);
    return () => clearTimeout(t);
  }, [loadProducts]);

  // CS 21.72 -- Fetch the FULL category taxonomy for this collection once
  // the viewer authenticates. Uses the surface's entire catalog_products
  // dataset (not the loaded page). Alias-normalised server-side.
  useEffect(() => {
    if (!viewer || !pin) return;
    axios.get(`${PUBLIC_API}/public/dist/${kind}/${token}/categories`, { params: { pin } })
      .then((r) => setCategoryOptions(r.data?.categories || []))
      .catch(() => setCategoryOptions([]));
  }, [viewer, kind, token, pin]);

  const submitPin = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4}$/.test(pinInput)) return setAuthError("PIN must be 4 digits");
    try {
      const { data } = await axios.post(`${PUBLIC_API}/public/dist/${kind}/${token}/verify-pin`, { pin: pinInput });
      setPin(data.session);
      sessionStorage.setItem(STORAGE_PIN_KEY, data.session);
      setViewer(data.viewer);
      setAuthError("");
      toast.success("Welcome");
    } catch (err) {
      if (err.response?.status === 410) setLinkInactive(true);
      setAuthError(err.response?.data?.detail || "Incorrect PIN");
    }
  };

  // CS 20.3 — filtered & sorted view of items. Selection (qty + colour) is NOT
  // re-computed by the filter; users may filter, then clear the filter, and still
  // see their previous picks. Totals are computed from the full `items` list below,
  // so filtering does not move the bottom-tray numbers.
  const filteredItems = useMemo(() => applyFiltersAndSort(items, filters), [items, filters]);
  const bounds = useMemo(() => priceBounds(items), [items]);
  const totals = useMemo(() => {
    const designs = Object.values(qty).filter((q) => q > 0).length;
    const pieces = Object.values(qty).reduce((a, q) => a + (parseInt(q) || 0), 0);
    let value = 0;
    if (items) {
      // Order value uses YOUR cost tier: distributor cost (dist_owner) or retailer cost otherwise
      for (const p of items) {
        const code = p.product_code || p.jewel_code;
        const q = parseInt(qty[code]) || 0;
        if (!q) continue;
        const price = viewer === "dist_owner" ? p.pricing.distributor
                     : (viewer === "retailer_staff" ? p.pricing.customer_mrp : p.pricing.retailer);
        value += (price || 0) * q;
      }
    }
    return { designs, pieces, value };
  }, [qty, items, viewer]);

  const titleText = kind === "distributor" ? "Distributor Collection" : "Retailer Collection";

  // CS 20.8 — inactive recipient link → friendly dead-end screen
  if (linkInactive) {
    return (
      <div className="min-h-screen bg-[#F7F1E6] flex items-center justify-center p-6" data-testid="dist-inactive-screen">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-10 border border-[#b8893b]/15 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#b8893b] mb-3" style={{ fontFamily: "'Jost', sans-serif" }}>
            Ashth · Distribution
          </p>
          <h1 className="text-3xl text-[#2a2520] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            This link is no longer active
          </h1>
          <p className="text-sm text-slate-500">Please request a fresh link from your point of contact at Ashth.</p>
        </div>
      </div>
    );
  }

  // ── PIN screen ──
  if (!viewer) {
    return (
      <div className="min-h-screen bg-[#F7F1E6] flex items-center justify-center p-6" data-testid="dist-pin-screen">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#b8893b] mb-3" style={{ fontFamily: "'Jost', sans-serif" }}>
              Ashth · Luxury Designer Lab Diamond Jewellery
            </p>
            <h1 className="text-5xl text-[#2a2520] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {titleText}
            </h1>
            {recipientName && (
              <p className="text-xs uppercase tracking-[0.25em] text-[#b8893b] mt-2 mb-1" data-testid="dist-recipient-name-pin">
                Prepared for · {recipientName}
              </p>
            )}
            <p className="text-sm text-slate-500" style={{ fontFamily: "'Jost', sans-serif" }}>Enter the PIN to view assortment</p>
          </div>
          <form onSubmit={submitPin} className="bg-white rounded-lg shadow-lg p-8 border border-[#b8893b]/15">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[#b8893b]" />
              <span className="text-xs uppercase tracking-widest text-slate-500">Secure access</span>
            </div>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4)); setAuthError(""); }}
              placeholder="4-digit PIN"
              className="text-center text-2xl tracking-[1em] h-14 font-mono"
              data-testid="dist-pin-input"
              autoFocus
            />
            {authError && <p className="text-xs text-red-600 mt-2" data-testid="dist-pin-error">{authError}</p>}
            <Button type="submit" className="w-full mt-5 bg-[#2a2520] hover:bg-[#3a342d] text-white h-12 tracking-widest text-sm" data-testid="dist-pin-submit">
              ENTER
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main catalogue ──
  return (
    <div className="min-h-screen bg-[#F7F1E6] pb-32" style={{ fontFamily: "'Jost', sans-serif" }} data-testid="dist-collection-page">
      {/* Header */}
      <header className="text-center pt-12 pb-8 px-4">
        <p className="text-[10px] uppercase tracking-[0.35em] text-[#b8893b]">
          Ashth · Luxury Designer Lab Diamond Jewellery
        </p>
        <h1 className="text-6xl text-[#2a2520] mt-3 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="dist-collection-title">
          {titleText}
        </h1>
        {recipientName && (
          <p className="text-xs uppercase tracking-[0.25em] text-[#b8893b] mb-1" data-testid="dist-recipient-name">
            Prepared for · {recipientName}
          </p>
        )}
        <p className="text-sm text-slate-500">Select your assortment — toggle purity, browse, add quantities</p>
      </header>

      {/* Sticky controls bar */}
      <div className="sticky top-0 z-30 bg-[#F7F1E6]/95 backdrop-blur border-y border-[#2a2520]/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
          {/* Purity toggle */}
          <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-[#2a2520]/10">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#b8893b] font-semibold pr-1">Purity</span>
            {PURITIES.map((p) => (
              <button
                key={p}
                onClick={() => setPurity(p)}
                data-testid={`purity-${p}`}
                className={`px-3 py-1 text-xs tracking-wide rounded-full transition ${
                  purity === p ? "bg-[#b8893b] text-white" : "text-slate-600 hover:bg-[#b8893b]/10"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          {/* CS 20.5 — Driving 24kt gold rate (live; from Distribution rates) */}
          {goldRate24kt != null && (
            <div
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#2a2520]/10"
              data-testid="dist-gold-rate"
              title="Live 24kt gold rate driving all Cost / MRP calculations"
            >
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#b8893b] font-semibold pr-1">Gold rate</span>
              <span className="text-xs text-[#0D1B2A] font-medium">₹{formatIndian(goldRate24kt, { decimals: 0 })} / g</span>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">24kt</span>
            </div>
          )}
          {/* CS 21.72 -- Category chips populated from the server-side full
              taxonomy of this collection's catalog_products. Alias-collapsed
              (Nosepin + Nose Pin → single "Nose Pin" with combined count). */}
          <div className="flex items-center gap-1 bg-white rounded-full px-2 py-1 border border-[#2a2520]/10 flex-wrap"
               data-testid="dist-category-chips">
            <button
              onClick={() => setCategory("All")}
              data-testid="cat-All"
              className={`px-3 py-1.5 text-xs tracking-wide rounded-full transition ${
                category === "All" ? "bg-[#2a2520] text-white" : "text-slate-600 hover:bg-[#2a2520]/5"
              }`}
            >
              All
            </button>
            {categoryOptions.map((c) => {
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`cat-${c.label}`}
                  className={`px-3 py-1.5 text-xs tracking-wide rounded-full transition flex items-center gap-1.5 ${
                    active ? "bg-[#2a2520] text-white" : "text-slate-600 hover:bg-[#2a2520]/5"
                  }`}
                >
                  <span>{c.label}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      active ? "bg-white/25 text-white" : "bg-[#2a2520]/5 text-slate-500"
                    }`}
                  >
                    {c.count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search style no…"
              className="pl-9 bg-white rounded-full border-[#2a2520]/10 h-10"
              data-testid="dist-search"
            />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <CatalogFilterBar
              filters={filters}
              onChange={setFilters}
              bounds={bounds}
            />
            <div className="text-xs text-slate-600 tabular-nums" data-testid="dist-count">
              <span className="font-semibold">{filteredItems ? filteredItems.length : "…"}</span> designs · {purity}
            </div>
          </div>
        </div>
      </div>

      {/* Product grid */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {items === null ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <div key={`skel-${i}`} className="aspect-[3/5] bg-white/40 rounded animate-pulse" />)}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500 text-sm">{items.length === 0 ? "No designs in this catalogue yet." : "No designs match the current filters."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="dist-grid">
            {filteredItems.map((p) => {
              const code = p.product_code || p.jewel_code;
              return (
                <ProductCard
                  key={code}
                  p={p}
                  code={code}
                  kind={kind}
                  viewer={viewer}
                  qty={qty[code] || 0}
                  onQty={(n) => setQty((q) => ({ ...q, [code]: Math.max(0, n) }))}
                  colour={colour[code] || "Yellow"}
                  onColour={(c) => setColour((m) => ({ ...m, [code]: c }))}
                />
              );
            })}
          </div>
        )}
      </main>

      {/* Sticky bottom tray — CS 20.3: right padding reserved for the "Made with Emergent" badge.
          Buttons centered on desktop; stats on the left; right safe-area prevents overlap. */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#2a2520] text-white shadow-2xl z-40 px-6 py-4" data-testid="dist-tray">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4 pr-2 md:pr-40">
          <div className="flex flex-wrap gap-6 md:gap-8">
            <Stat label="Designs" value={totals.designs} testid="tray-designs" />
            <Stat label="Total pieces" value={totals.pieces} testid="tray-pieces" />
            <Stat label={`Order value · ${
              viewer === "dist_owner" ? "Distributor"
              : viewer === "dist_staff" ? "Retailer cost"
              : viewer === "retailer_staff" ? "MRP"
              : "Your cost"
            }`} value={`₹${formatIndian(totals.value)}`} testid="tray-value" />
          </div>
          <div className="flex gap-2 mx-auto md:mx-0 md:ml-auto">
            <Button
              variant="outline"
              onClick={() => { if (window.confirm("Clear all selected quantities and colour choices?")) { setQty({}); setColour({}); } }}
              className="border-white/40 text-white hover:bg-white/10 hover:text-white tracking-widest text-xs"
              data-testid="tray-clear"
            >CLEAR</Button>
            <Button
              variant="outline"
              onClick={() => setShowReview(true)}
              disabled={totals.designs === 0}
              className="border-white/40 text-white hover:bg-white/10 hover:text-white tracking-widest text-xs disabled:opacity-30"
              data-testid="tray-review"
            >REVIEW</Button>
            <Button
              onClick={() => exportCsv(items, qty, viewer, purity, colour, recipientName, kind)}
              disabled={totals.designs === 0}
              className="bg-[#b8893b] hover:bg-[#a37730] text-white tracking-widest text-xs disabled:opacity-30"
              data-testid="tray-export"
            >EXPORT SELECTION</Button>
          </div>
        </div>
      </div>

      {/* Review modal */}
      <ReviewModal
        open={showReview}
        onClose={() => setShowReview(false)}
        items={items || []}
        qty={qty}
        colour={colour}
        viewer={viewer}
        purity={purity}
        totals={totals}
        recipientName={recipientName}
        kind={kind}
      />
    </div>
  );
}

// ──────────────────────────── Product Card ────────────────────────────
function ProductCard({ p, code, kind, viewer, qty, onQty, colour, onColour }) {
  const showDist = "distributor" in p.pricing;
  const showRetailer = "retailer" in p.pricing;
  const distGp = p.pricing.distributor_gp_pct;
  const retGp = p.pricing.retailer_gp_pct;
  const selected = qty > 0;

  return (
    <div
      className={`relative bg-white rounded-md border transition shadow-sm overflow-hidden ${
        selected ? "border-[#b8893b] ring-2 ring-[#b8893b]/30" : "border-[#2a2520]/10"
      }`}
      data-testid={`dist-card-${code}`}
    >
      {selected && (
        <div className="absolute top-2 right-2 z-10 bg-[#b8893b] text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold tracking-wider">
          <Check className="w-3 h-3" /> {qty}
        </div>
      )}
      {p.is_memo && (
        <div className="absolute top-2 left-2 z-10 bg-emerald-600 text-white text-[9px] px-2 py-0.5 rounded-full tracking-widest uppercase font-semibold">
          ● Memo
        </div>
      )}
      {p.is_sold && (
        <div className="absolute top-2 left-2 z-10 bg-slate-500 text-white text-[9px] px-2 py-0.5 rounded-full tracking-widest uppercase font-semibold">
          Sold
        </div>
      )}

      {/* Image */}
      <div className="aspect-square bg-white flex items-center justify-center p-4">
        {p.photo_url ? (
          <img
            src={p.photo_url}
            alt={code}
            loading="lazy"
            className="max-w-full max-h-full object-contain"
            style={{ imageRendering: "auto" }}
          />
        ) : (
          <div className="w-3/5 h-3/5 rounded bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-[10px] text-slate-400 tracking-widest">
            NO IMAGE
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-4 border-t border-[#2a2520]/10">
        <p className="text-lg text-[#2a2520]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{code}</p>
        <div className="flex gap-1 mt-1">
          {p.category && <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#b8893b]/30 text-[#b8893b] uppercase tracking-wider">{p.category}</span>}
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#2a2520]/15 text-slate-600 uppercase tracking-wider">{p.pricing.purity}</span>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 tabular-nums">
          Gold {formatIndian(p.pricing.gold_weight, { decimals: 2 })} g · {p.pricing.purity?.replace("KT", "kt").toLowerCase()} · Dia {formatIndian(p.pricing.diamond_carat, { decimals: 2 })} ct
        </p>
        {(Number(p.pricing.diamond_pcs) > 0 || Number(p.pricing.diamond_carat) > 0) && (
          <p className="text-[11px] text-slate-500 flex items-center justify-between mt-1 tabular-nums" data-testid={`public-card-diamonds-${code}`}>
            <span className="uppercase tracking-wider text-[10px] text-slate-400">Diamonds</span>
            <span>
              {Number(p.pricing.diamond_pcs) > 0 && `${formatIndian(p.pricing.diamond_pcs)} pcs · `}
              {formatIndian(p.pricing.diamond_carat, { decimals: 3 })} ct
            </span>
          </p>
        )}

        {/* Prices */}
        <div className="border-t border-dashed border-slate-200 mt-3 pt-3">
          {showDist && showRetailer && (
            // distributor owner: 3 columns
            <div className="grid grid-cols-3 gap-2 text-center">
              <PriceCol label="Distributor" value={p.pricing.distributor} tone="brand" />
              <PriceCol label="Retailer" value={p.pricing.retailer} tone="brand" />
              <PriceCol label="Cust. MRP" value={p.pricing.customer_mrp} tone="green" />
            </div>
          )}
          {!showDist && showRetailer && (
            // CS 20.6 — Distributor-staff vs Retailer-owner labels differ.
            // Spec: distributor-link STAFF must read "Retailer Cost" + "Retailer GP%";
            // retailer-link OWNER reads "Your Cost" + "Your Margin" (their cost IS the retailer tier).
            <div className="grid grid-cols-2 gap-2 text-center">
              <PriceCol
                label={viewer === "dist_staff" ? "Retailer Cost" : "Your Cost"}
                value={p.pricing.retailer}
                tone="brand"
              />
              <PriceCol label="Customer MRP" value={p.pricing.customer_mrp} tone="green" />
            </div>
          )}
          {!showDist && !showRetailer && (
            // retailer staff: 1 column
            <div className="text-center">
              <PriceCol label="Customer MRP" value={p.pricing.customer_mrp} tone="green" big />
            </div>
          )}
        </div>

        {/* GP row -- CS 21.80: hidden for AL-series (server drops the GP
            fields, so the null guards below fail-closed for AL pieces). */}
        <div className="mt-2 text-[11px] text-slate-500">
          {viewer === "dist_owner" && distGp != null && (
            <span data-testid={`gp-${code}`}>
              Dist GP <span className="text-[#0d6e6e] font-semibold">{distGp.toFixed(1)}%</span> · Retailer GP <span className="text-[#0d6e6e] font-semibold">{retGp?.toFixed(1)}%</span>
            </span>
          )}
          {viewer === "dist_staff" && retGp != null && (
            <span data-testid={`gp-${code}`}>
              Retailer GP <span className="text-[#0d6e6e] font-semibold">{retGp.toFixed(1)}%</span>
            </span>
          )}
          {viewer === "retailer_owner" && retGp != null && (
            <span data-testid={`gp-${code}`}>
              Your Margin <span className="text-[#0d6e6e] font-semibold">{retGp.toFixed(1)}%</span>
            </span>
          )}
        </div>
      </div>

      {/* Quantity bar */}
      <div className="grid grid-cols-3 border-t border-[#2a2520]/10">
        <button
          onClick={() => onQty(qty - 1)}
          disabled={qty <= 0}
          className="py-3 text-[#b8893b] disabled:text-slate-300 hover:bg-[#b8893b]/5 flex items-center justify-center"
          data-testid={`qty-minus-${code}`}
          aria-label="Decrease quantity"
        >
          <Minus className="w-4 h-4" />
        </button>
        <input
          type="number"
          min={0}
          value={qty}
          onChange={(e) => onQty(parseInt(e.target.value) || 0)}
          className="text-center text-base text-[#2a2520] tabular-nums focus:outline-none bg-transparent"
          data-testid={`qty-input-${code}`}
          aria-label="Quantity"
        />
        <button
          onClick={() => onQty(qty + 1)}
          className="py-3 bg-[#2a2520] text-white hover:bg-[#3a342d] flex items-center justify-center"
          data-testid={`qty-plus-${code}`}
          aria-label="Increase quantity"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Gold colour selector (CS 20.2). Defaults to Yellow per spec. Independent of qty. */}
      <div className="grid grid-cols-2 border-t border-[#2a2520]/10" data-testid={`colour-row-${code}`}>
        <button
          type="button"
          onClick={() => onColour("Yellow")}
          aria-pressed={colour === "Yellow"}
          data-testid={`colour-yellow-${code}`}
          className={`py-2.5 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition ${
            colour === "Yellow"
              ? "bg-[#b8893b] text-white"
              : "bg-white text-slate-500 hover:bg-[#b8893b]/5"
          }`}
        >
          <span className={`w-3 h-3 rounded-full ${colour === "Yellow" ? "bg-white border-2 border-white" : "bg-[#e8c980] border border-[#b8893b]/40"}`} />
          Yellow
        </button>
        <button
          type="button"
          onClick={() => onColour("White")}
          aria-pressed={colour === "White"}
          data-testid={`colour-white-${code}`}
          className={`py-2.5 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 border-l border-[#2a2520]/10 transition ${
            colour === "White"
              ? "bg-[#b8893b] text-white"
              : "bg-white text-slate-500 hover:bg-[#b8893b]/5"
          }`}
        >
          <span className={`w-3 h-3 rounded-full ${colour === "White" ? "bg-white border-2 border-white" : "bg-slate-100 border border-slate-300"}`} />
          White
        </button>
      </div>
    </div>
  );
}

function PriceCol({ label, value, tone, big }) {
  if (value === undefined || value === null) return null;
  const cls = tone === "green" ? "text-emerald-700" : "text-[#b8893b]";
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`tabular-nums font-semibold ${cls} ${big ? "text-base" : "text-sm"}`}>₹{formatIndian(value)}</p>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[9px] uppercase tracking-widest text-white/60">{label}</p>
      <p className="text-base font-semibold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

// ──────────────────────────── Review Modal ────────────────────────────
function ReviewModal({ open, onClose, items, qty, colour, viewer, purity, totals, recipientName, kind }) {
  const rows = useMemo(() => {
    return items
      .map((p) => {
        const code = p.product_code || p.jewel_code;
        return { p, code, q: parseInt(qty[code]) || 0, c: colour[code] || "Yellow" };
      })
      .filter((x) => x.q > 0);
  }, [items, qty, colour]);

  const copySummary = () => {
    const lines = [];
    if (recipientName) {
      const linkLabel = kind === "distributor" ? "Distributor Link" : "Retailer Link";
      lines.push(`Ashth — Prepared for ${recipientName} (${linkLabel})`);
    }
    lines.push(`Ashth — ${purity.toUpperCase()} Selection`);
    lines.push(`Designs: ${totals.designs} · Pieces: ${totals.pieces}`);
    lines.push(`Order Value: ₹${formatIndian(totals.value)}`);
    lines.push("");
    for (const { p, code, q, c } of rows) {
      lines.push(`${code} (${p.category}) ×${q} · ${c}`);
    }
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Summary copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dist-review-modal">
        <DialogHeader>
          <DialogTitle className="text-3xl text-[#2a2520]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Review Your Selection
          </DialogTitle>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="text-center text-slate-500 py-12">No items selected yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-[#2a2520]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-600 border-b border-[#2a2520]/15">
                    <th className="text-left py-3 pr-2">Product Code</th>
                    <th className="text-left py-3 px-2">Category</th>
                    <th className="text-left py-3 px-2">Colour</th>
                    <th className="text-right py-3 px-2">Gold (g)</th>
                    <th className="text-right py-3 px-2">Dia (ct)</th>
                    <th className="text-right py-3 px-2">Unit Price</th>
                    <th className="text-right py-3 px-2">Qty</th>
                    <th className="text-right py-3 pl-2">Line Total</th>
                  </tr>
                </thead>
                <tbody data-testid="dist-review-rows">
                  {rows.map(({ p, code, q, c }) => {
                    const unit = viewer === "dist_owner" ? p.pricing.distributor
                              : (viewer === "retailer_staff" ? p.pricing.customer_mrp : p.pricing.retailer);
                    return (
                      <tr key={code} className="border-b border-slate-100 hover:bg-[#F7F1E6]/40">
                        <td className="py-2.5 pr-2 font-medium">{code}</td>
                        <td className="py-2.5 px-2 text-slate-600">{p.category}</td>
                        <td className="py-2.5 px-2" data-testid={`review-colour-${code}`}>
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border ${
                            c === "Yellow"
                              ? "border-[#b8893b]/40 bg-[#b8893b]/8 text-[#8a6628]"
                              : "border-slate-300 bg-slate-50 text-slate-600"
                          }`}>
                            <span className={`w-2.5 h-2.5 rounded-full ${c === "Yellow" ? "bg-[#e8c980]" : "bg-slate-200 border border-slate-300"}`} />
                            {c}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{formatIndian(p.pricing.gold_weight, { decimals: 2 })}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{formatIndian(p.pricing.diamond_carat, { decimals: 2 })}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">₹{formatIndian(unit)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums font-semibold">{q}</td>
                        <td className="py-2.5 pl-2 text-right tabular-nums font-semibold">₹{formatIndian((unit || 0) * q)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#2a2520] text-white">
                    <td colSpan={6} className="py-3 px-2 text-xs uppercase tracking-widest">Grand Total</td>
                    <td className="py-3 px-2 text-right tabular-nums font-semibold">{totals.pieces}</td>
                    <td className="py-3 pl-2 text-right tabular-nums font-semibold">₹{formatIndian(totals.value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-[#2a2520]/10">
              <Button variant="outline" onClick={onClose} className="text-xs tracking-wider" data-testid="review-keep-browsing">
                <X className="w-3 h-3 mr-1" /> KEEP BROWSING
              </Button>
              <Button variant="outline" onClick={copySummary} className="text-xs tracking-wider" data-testid="review-copy">
                <Copy className="w-3 h-3 mr-1" /> COPY SUMMARY
              </Button>
              <Button onClick={() => exportCsv(items, qty, viewer, purity, colour, recipientName, kind)} className="bg-[#b8893b] hover:bg-[#a37730] text-white text-xs tracking-wider" data-testid="review-csv">
                <Download className="w-3 h-3 mr-1" /> DOWNLOAD CSV
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────── CSV export ────────────────────────────
function exportCsv(items, qty, viewer, purity, colour, recipientName, kind) {
  if (!items) return;
  const rows = items.map((p) => {
    const code = p.product_code || p.jewel_code;
    return { p, code, q: parseInt(qty[code]) || 0, c: (colour && colour[code]) || "Yellow" };
  }).filter((x) => x.q > 0);
  if (rows.length === 0) { toast.error("Select at least one product first"); return; }

  // CS 20.8 — Recipient stamping. Prepend a 2-line header so the admin (and
  // anyone receiving the file) can attribute the selection to a named
  // recipient. Also add a per-row Recipient column so spreadsheets that ignore
  // the preamble still carry the attribution.
  const out = [];
  if (recipientName) {
    const linkLabel = kind === "distributor" ? "Distributor Link" : "Retailer Link";
    out.push(csv(`Recipient: ${recipientName} (${linkLabel})`));
    out.push("");
  }

  const header = ["Recipient", "Purity", "Product Code", "Category", "Gold Colour", "Gold (g)", "Diamond (ct)", "Qty"];
  if (viewer === "dist_owner") header.push("Distributor Price", "Retailer Price", "Customer MRP", "Distributor GP %", "Retailer GP %", "Line Total (Dist)");
  else if (viewer === "dist_staff" || viewer === "retailer_owner") header.push("Retailer Price", "Customer MRP", "Retailer GP %", "Line Total (Retailer)");
  else header.push("Customer MRP", "Line Total (MRP)");
  out.push(header.join(","));

  let grandTotal = 0;
  let grandPieces = 0;
  const recCol = csv(recipientName || "");
  for (const { p, code, q, c } of rows) {
    const pr = p.pricing;
    const unit = viewer === "dist_owner" ? pr.distributor : (viewer === "retailer_staff" ? pr.customer_mrp : pr.retailer);
    const lineTotal = (unit || 0) * q;
    grandTotal += lineTotal;
    grandPieces += q;
    const base = [recCol, pr.purity, csv(code), csv(p.category), c, pr.gold_weight, pr.diamond_carat, q];
    if (viewer === "dist_owner") base.push(pr.distributor, pr.retailer, pr.customer_mrp, pr.distributor_gp_pct, pr.retailer_gp_pct, lineTotal.toFixed(2));
    else if (viewer === "dist_staff" || viewer === "retailer_owner") base.push(pr.retailer, pr.customer_mrp, pr.retailer_gp_pct ?? "", lineTotal.toFixed(2));
    else base.push(pr.customer_mrp, lineTotal.toFixed(2));
    out.push(base.join(","));
  }
  const totalCols = header.length;
  const totalRow = ["GRAND TOTAL", "", "", "", "", "", "", grandPieces];
  while (totalRow.length < totalCols - 1) totalRow.push("");
  totalRow.push(grandTotal.toFixed(2));
  out.push(totalRow.join(","));

  const blob = new Blob([out.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = (recipientName || viewer).replace(/[^A-Za-z0-9_-]+/g, "_");
  a.download = `Ashth_${slug}_${purity}_${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success("CSV downloaded");
}

function csv(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
}
